import { ContextTracker, ExternalTokenizer } from "@lezer/lr";
import {
  BlockComment,
  braceR,
  DocComment,
  insertSemi,
  LineComment,
  newline as newlineTerm,
  space,
} from "./rhai.grammar.terms";

// External tokens for rhai.grammar: what a regular token rule can't say.

const SLASH = 47;
const STAR = 42;
const BRACE_R = 125;
const EOF = -1;

// Rhai block comments nest (`/* a /* b */ c */` is one comment), so the
// depth is counted by hand.
export const blockComment = new ExternalTokenizer((input) => {
  if (input.next !== SLASH || input.peek(1) !== STAR) return;
  input.advance(2);
  let depth = 1;
  while (depth > 0) {
    const ch: number = input.next;
    if (ch === EOF) break;
    const pair = ch * 256 + input.peek(1);
    if (pair === SLASH * 256 + STAR) {
      input.advance(2);
      depth++;
    } else if (pair === STAR * 256 + SLASH) {
      input.advance(2);
      depth--;
    } else {
      input.advance();
    }
  }
  input.acceptToken(BlockComment);
});

// What the virtual semicolon needs to know about the tokens before it:
// whether the last real token closed a block, and whether a line break has
// passed since it. Skipped tokens (space, comments) leave both alone.
interface SemiContext {
  afterBrace: boolean;
  newline: boolean;
}

const SKIPPED = new Set([space, LineComment, BlockComment, DocComment]);

export const trackSemi = new ContextTracker<SemiContext>({
  start: { afterBrace: false, newline: false },
  shift(context, term) {
    if (term === newlineTerm) {
      return context.newline ? context : { ...context, newline: true };
    }
    if (SKIPPED.has(term)) return context;
    return { afterBrace: term === braceR, newline: false };
  },
  hash: (context) => (context.afterBrace ? 1 : 0) + (context.newline ? 2 : 0),
  strict: false,
});

const isSpace = (ch: number) => ch === 32 || ch === 9 || ch === 10 || ch === 13;

// A statement may end without `;` after a `}` (block-ending expressions),
// at a line break, and before a `}` or the end of the script: a zero-width
// token the grammar accepts wherever `;` would be. Runs as a fallback, so
// a real token that continues the expression (an `else`, an operator)
// wins over it.
export const insertSemicolon = new ExternalTokenizer(
  (input, stack) => {
    if (!stack.canShift(insertSemi)) return;
    const context = stack.context as SemiContext;
    let ahead = 0;
    while (isSpace(input.peek(ahead))) ahead++;
    const next = input.peek(ahead);
    if (
      next === BRACE_R ||
      next === EOF ||
      context.afterBrace ||
      context.newline
    ) {
      input.acceptToken(insertSemi);
    }
  },
  { contextual: true, fallback: true },
);
